import { useCallback, useEffect, useMemo, useState } from 'react';

// Rows per page until the table has measured how many fit on screen
const DEFAULT_PAGE_SIZE = 10;
const defaultRowId = (row) => row.id;

/**
 * Client-side paging of an already filtered list. `resetKey` changes (a new
 * search or filter) send it back to the first page; the page also stays in
 * range when the list shrinks or fewer rows fit.
 */
const usePagedList = (rows, { resetKey, getRowId = defaultRowId, initialPageSize = DEFAULT_PAGE_SIZE } = {}) => {
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setCurrentPage(1);
  }, [resetKey]);

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const page = Math.min(currentPage, totalPages);
  const pageStart = (page - 1) * pageSize;
  const pageRows = useMemo(() => rows.slice(pageStart, pageStart + pageSize), [rows, pageStart, pageSize]);

  const goToPage = useCallback((target) => {
    if (target >= 1 && target <= totalPages) setCurrentPage(target);
  }, [totalPages]);

  /** Shows the page holding this row; false when the list doesn't contain it. */
  const reveal = useCallback((id) => {
    const index = rows.findIndex((row) => getRowId(row) === id);
    if (index < 0) return false;
    setCurrentPage(Math.floor(index / pageSize) + 1);
    return true;
  }, [rows, pageSize, getRowId]);

  return { pageRows, pageStart, currentPage: page, totalPages, pageSize, setPageSize, goToPage, reveal };
};

export default usePagedList;
